import firebase from "../constants/firebase";
import { STUDENTS } from "../constants/collections";

const ENROLLMENTS_SUBCOLLECTION = "enrollments";

/**
 * Creates an admission application for a student for a specific school year.
 * This creates a document in `students/{studentId}/enrollments/{sy}` with status 'PENDING'.
 *
 * @param {string} studentId - The ID of the student
 * @param {string} sy - The School Year (e.g., "2025-2026")
 * @param {string} type - 'NEW', 'TRANSFEREE', 'RETURNEE'
 * @param {Object} additionalData - Any extra fields
 */
export const createAdmissionApplication = async (
  studentId,
  sy,
  type = "NEW",
  additionalData = {},
) => {
  const db = firebase.firestore();
  const enrollmentRef = db
    .collection(STUDENTS)
    .doc(studentId)
    .collection(ENROLLMENTS_SUBCOLLECTION)
    .doc(sy);

  const timestamp = firebase.firestore.FieldValue.serverTimestamp();

  const data = {
    studentId, // Redundant but useful for collection group queries if needed
    schoolYear: sy,
    type,
    admissionStatus: "PENDING", // PENDING, UNDER_REVIEW, ADMITTED, ASSESSED, ENROLLED, REJECTED
    billingStatus: "PENDING_ASSESSMENT",
    sectionId: null, // Will be filled upon final enrollment
    requirements: {}, // Map of requirements { form138: true, psa: false }
    audit: {
      created: timestamp,
      updated: timestamp,
    },
    ...additionalData,
  };

  await enrollmentRef.set(data, { merge: true });
  return enrollmentRef;
};

/**
 * Updates the admission status of a student for a specific school year.
 *
 * @param {string} studentId
 * @param {string} sy
 * @param {string} status - 'PENDING', 'UNDER_REVIEW', 'ADMITTED', 'ASSESSED', 'ENROLLED', 'REJECTED'
 */
export const updateAdmissionStatus = async (studentId, sy, status) => {
  const db = firebase.firestore();
  const enrollmentRef = db
    .collection(STUDENTS)
    .doc(studentId)
    .collection(ENROLLMENTS_SUBCOLLECTION)
    .doc(sy);

  await enrollmentRef.set(
    {
      admissionStatus: status,
      "audit.updated": firebase.firestore.FieldValue.serverTimestamp(),
      "audit.statusAuth":
        status === "ADMITTED"
          ? firebase.firestore.FieldValue.serverTimestamp()
          : null,
    },
    { merge: true },
  );

  return status;
};

/**
 * Gets the enrollment/admission record for a student in a specific SY.
 *
 * @param {string} studentId
 * @param {string} sy
 */
export const getAdmissionRecord = async (studentId, sy) => {
  const db = firebase.firestore();
  const doc = await db
    .collection(STUDENTS)
    .doc(studentId)
    .collection(ENROLLMENTS_SUBCOLLECTION)
    .doc(sy)
    .get();

  if (!doc.exists) return null;
  return doc.data();
};

/**
 * Checks if a student is admitted (Gatekeeper function).
 *
 * @param {string} studentId
 * @param {string} sy
 * @returns {Promise<boolean>}
 */
export const isStudentAdmitted = async (studentId, sy) => {
  const record = await getAdmissionRecord(studentId, sy);
  if (!record) return false;

  // Allow if ADMITTED or already ENROLLED or ASSESSED
  return ["ADMITTED", "ASSESSED", "ENROLLED"].includes(
    record.admissionStatus,
  );
};

export const findStudentByLRN = async (lrn, birthdate) => {
  try {
    // OLD METHOD: Firestore Direct (Privileged)
    /*
    const db = firebase.firestore();
    const studentRef = db.collection(STUDENTS).where("lrn", "==", lrn);
    const snapshot = await studentRef.get();
    ...
    */

    // NEW METHOD: Public API
    // We use the Functions API because 'students' collection is not public.
    const api = process.env.REACT_APP_FUNCTIONS_API; // e.g. https://.../api
    const url = `${api}/admission/search?lrn=${lrn}&birthdate=${birthdate}`;

    const response = await fetch(url);
    if (response.status === 404) {
      return null; // Not found
    }
    if (response.status === 403) {
      const err = await response.json();
      // eslint-disable-next-line no-throw-literal
      throw { code: "VERIFICATION_FAILED", message: err.message };
    }
    if (!response.ok) {
      throw new Error("Search failed");
    }

    const studentData = await response.json();
    return studentData;
  } catch (error) {
    console.error("Error finding student:", error);
    throw error;
  }
};

export const verifySchoolAdmissionToken = async (token, sy = null) => {
  try {
    const api = process.env.REACT_APP_FUNCTIONS_API || "";
    // Pass sy so the Cloud Function can fetch admissionSemester server-side
    // (the public page cannot read schools/{id}/settings directly — rules require auth)
    const syParam = sy ? `&sy=${encodeURIComponent(sy)}` : "";
    const url = `${api}/admission/verify-token?token=${token}${syParam}`;

    const response = await fetch(url);
    if (!response.ok) {
      if (response.status === 404) return null; // Invalid token
      throw new Error("Verification failed");
    }
    const schoolData = await response.json();
    return schoolData;
  } catch (error) {
    console.error("Error verifying token:", error);
    return null;
  }
};
